import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { RefSplitOrder, SpinReferenceKind } from '../../../Repository/Admin/AdminSpinRepository.js'

/**
 * A generic keyed response cache for the admin SPIN panel.
 *
 * Deliberately ONE slice for all ~12 endpoints rather than a slice + epic + selector set per endpoint.
 * The panel is admin-only, read-only, and single-consumer — none of the things a per-domain slice buys
 * you (shared cross-page state, targeted reducers, optimistic updates) apply. What it does need is that
 * navigating between sub-pages stops re-issuing queries that take seconds: `/metrics` alone is mounted by
 * four pages, and the opt-in whole-set queries (cohesion, leaderboard, singletons) were being thrown away
 * the moment you clicked into a SPIN and back.
 *
 * The cache is session-lifetime with no TTL. That is a deliberate fit to the data, not laziness: SPIN
 * rows are immutable (`spin.forbid_mutation` trigger), and everything else here is keyed by a run
 * timestamp, so a given key's answer cannot change underneath us. Only the run LIST can go stale, and a
 * new run landing mid-session is rare enough that the explicit Reload buttons cover it.
 *
 * **The cache key is the correctness-critical part.** Every parameter that shapes an answer must appear
 * in [cacheKey], or the panel will show one question's data under another question's label — strictly
 * worse than the refetch this replaces. [AdminSpinQuery] is a discriminated union precisely so that
 * adding a parameter without adding it to the key is a type error rather than a silent bug.
 */

/** Every query the admin SPIN panel can issue. One variant per endpoint; fields are the cache key. */
export type AdminSpinQuery =
  | { kind: 'runs' }
  | { kind: 'metrics'; algorithmType: string; asOf: string; amplicon: string | null }
  | { kind: 'cohesion'; algorithmType: string; asOf: string; amplicon: string | null }
  | { kind: 'leaderboard'; algorithmType: string; asOf: string; amplicon: string | null; limit?: number }
  | {
      kind: 'singletons'
      algorithmType: string
      asOf: string
      amplicon: string | null
      minHitPercentage?: number
      limit?: number
    }
  | {
      kind: 'reference'
      algorithmType: string
      asOf: string
      amplicon: string | null
      refKind: SpinReferenceKind
      orderBy?: RefSplitOrder
      minHitPercentage?: number
    }
  | {
      kind: 'referenceDetail'
      algorithmType: string
      asOf: string
      amplicon: string | null
      refKind: SpinReferenceKind
      refId: string
      limit?: number
    }
  | { kind: 'compare'; a: string; b: string; amplicon: string | null }
  | { kind: 'drift'; runId: string; rank?: number; source?: string }
  | { kind: 'lineage'; algorithmType: string }
  | { kind: 'membershipFlow'; runId: string }
  | { kind: 'detail'; spinId: string }

export type AdminSpinQueryKind = AdminSpinQuery['kind']

/**
 * Stable cache key for a query. Derived from ALL of the descriptor's fields (sorted, so the key does not
 * depend on literal declaration order) — never a hand-maintained list, which is how a key drifts out of
 * step with the parameters it is supposed to describe.
 *
 * `null` and `undefined` collapse to the same token on purpose: across this API "no amplicon filter" is
 * expressed both ways depending on the call site, and they mean the same query.
 */
export const cacheKey = (query: AdminSpinQuery): string => {
  const record = query as unknown as Record<string, unknown>
  const parts = Object.keys(record)
    .filter((k) => k !== 'kind')
    .sort()
    .map((k) => `${k}=${record[k] ?? ''}`)
  return [query.kind, ...parts].join('|')
}

export type AdminSpinStatus = 'loading' | 'ready' | 'error'

/** One cached response. `data` is untyped here; the hook that owns the query casts it back. */
export interface IAdminSpinEntry {
  status: AdminSpinStatus
  data?: unknown
  error?: string
}

export interface IAdminSpinState {
  entries: Record<string, IAdminSpinEntry>
}

const initialState: IAdminSpinState = { entries: {} }

export const adminSpinSlice = createSlice({
  name: 'adminSpin',
  initialState,
  reducers: {
    /**
     * Request a query. Epic-only, hence the identity reducer: whether this results in a fetch depends on
     * what is already cached, and that decision lives in the epic alone so it cannot be made two
     * different ways. `force` bypasses the cache — what the pages' explicit Reload buttons dispatch.
     *
     * Declared via `prepare` rather than a typed-but-unused reducer argument, which is the RTK way to
     * get a typed action creator for an action the reducer does not act on.
     */
    query: {
      reducer: (state: IAdminSpinState) => state,
      prepare: (payload: { query: AdminSpinQuery; force?: boolean }) => ({ payload })
    },

    queryStarted: (state, action: PayloadAction<{ key: string }>) => {
      // Preserve any previous `data` so a Reload shows the old table until the new one lands, rather
      // than blanking the page for the several seconds these queries take.
      const previous = state.entries[action.payload.key]
      state.entries[action.payload.key] = { status: 'loading', data: previous?.data }
    },

    queryFulfilled: (state, action: PayloadAction<{ key: string; data: unknown }>) => {
      state.entries[action.payload.key] = { status: 'ready', data: action.payload.data }
    },

    queryFailed: (state, action: PayloadAction<{ key: string; error: string }>) => {
      state.entries[action.payload.key] = { status: 'error', error: action.payload.error }
    },

    /** Drop everything — for a "hard refresh" affordance, and to keep tests isolated. */
    clearCache: () => initialState
  }
})
